// TODO: Add more tags such as ownership, return date etc
import React, { useState } from 'react';
import backIcon from '../images/arrow-left.svg';
import bookCover from '../images/empty-book.svg';
import './EditBook.css';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  generateBookKey,
  readingStatus,
  updateProtocol,
} from '../utils/functions';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import { useTranslation } from '../i18n';
import TextArea from './TextArea';
import StarRating from './StarRating';

const EditBook = ({
  book,
  backText,
  buttonText,
  fetchBooks,
  toggleEdit,
  input,
  options,
  user,
  required,
  showNotification,
  updateCurrentlyReading,
}) => {
  const [title, setTitle] = useState(
    book?.title || (options === 'intitle:' ? input : '') || '',
  );
  const [author, setAuthor] = useState(
    book?.author_name || (options === 'inauthor:' ? input : '') || '',
  );
  const [isbn, setISBN] = useState(
    book?.isbn || (options === 'isbn:' ? input : '') || '',
  );
  const [pages, setPages] = useState(book?.number_of_pages || '');
  const [published, setPublished] = useState(book?.publish_date || '');
  const [publisher, setPublisher] = useState(
    book?.publisher || (options === 'publisher:' ? input : '') || '',
  );
  const [coverurl, setCoverurl] = useState(book?.cover_url || '');
  const [review, setReview] = useState(book?.review || '');
  const [rating, setRating] = useState(book?.rating || '');
  const [status, setStatus] = useState('wantToRead');
  const [updatingBook, setUpdatingBook] = useState(false);

  const { t } = useTranslation();

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleAuthorChange = (e) => {
    setAuthor(e.target.value);
  };

  const handleISBNChange = (e) => {
    setISBN(e.target.value);
  };

  const handlePagesChange = (e) => {
    setPages(e.target.value);
  };

  const handlePublishedChange = (e) => {
    setPublished(e.target.value);
  };

  const handlePublisherChange = (e) => {
    setPublisher(e.target.value);
  };

  const handleCoverurlChange = (e) => {
    setCoverurl(e.target.value);
  };

  const handleReviewChange = (e) => {
    setReview(e.target.value);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !author || !pages) {
      showNotification(t.updatebookNotification);
      return false;
    }

    setUpdatingBook(true);
    const userBooksRef = collection(db, 'UserBooks');

    try {
      if (!required) {
        const q = query(
          userBooksRef,
          where('userID', '==', book?.userID),
          where('bookID', '==', book?.bookID),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Update book
          const doc = querySnapshot.docs[0];
          const docRef = doc.ref;

          const updateData = {
            ...book,
            title: title,
            author_name: author,
            isbn: isbn,
            number_of_pages: parseInt(pages),
            publish_date: published,
            publisher: publisher,
            review: review,
            rating: rating,
            cover_url: typeof coverurl === 'string' ? [coverurl] : coverurl,
          };

          await updateDoc(docRef, updateData);
          if (
            book?.status === 'reading' &&
            !isNaN(updateData.number_of_pages)
          ) {
            updateCurrentlyReading(updateData, user, true);
          }
        }
      } else {
        // Save book
        const userBook = {
          userID: user.uid,
          bookID: generateBookKey('_manual_'),
          cover_url: coverurl ? [coverurl] : [false],
          title: title,
          author_name: author,
          number_of_pages: parseInt(pages),
          publisher: publisher,
          publish_date: published,
          isbn: isbn,
          status: status,
          starred: false,
          review: '',
          rating: '',
          dateStarted: '',
          createdAt: serverTimestamp(),
        };
        await setDoc(doc(userBooksRef), userBook);

        // Update currentlyReading if status reading and book has pages
        if (status === 'reading' && !isNaN(userBook.number_of_pages)) {
          updateCurrentlyReading(userBook, user, true);
        }
      }
      if (!required) {
        showNotification(`${title} ${t.wasupdated}!`);
      } else {
        showNotification(`${title} ${t.savedto} ${readingStatus(status)}!`);
      }
      fetchBooks();
    } catch (error) {
      console.error('Error updating/saving UserBook: ', error);
    } finally {
      setUpdatingBook(false);
      toggleEdit();
    }
  };

  return (
    <div className="dialog-content fullscreen-overlay">
      <div className="fullscreen-container" tabIndex={-1}>
        <button className="fullscreen-close" onClick={() => toggleEdit()}>
          <img src={backIcon} width={42} height={42} alt="Close icon" />
          {backText}
        </button>

        <div className="image-wrapper">
          <img
            src={
              updateProtocol(book?.cover_url[0]) ||
              updateProtocol(coverurl) ||
              bookCover
            }
            alt={book?.title || t.missingcover}
            height={200}
            width={140}
            className="card-edit-image"
          />
        </div>
        <form onSubmit={handleSubmit} className="form">
          <h1 className="h1">{t.bookdetails}</h1>
          <TextInput
            type="text"
            labelName={t.titleLabel}
            id="title"
            value={title}
            onChangeFunction={handleTitleChange}
            required={true}
          />
          <TextInput
            type="text"
            labelName={t.authorLabel}
            id="author"
            value={author}
            onChangeFunction={handleAuthorChange}
            required={true}
          />
          <TextInput
            type="text"
            labelName={t.isbn}
            id="isbn"
            value={isbn}
            onChangeFunction={handleISBNChange}
            inputmode="numeric"
          />
          <TextInput
            type="number"
            labelName={t.pages}
            id="pages"
            value={pages}
            onChangeFunction={handlePagesChange}
            required={true}
            inputmode="numeric"
          />
          <TextInput
            type="text"
            labelName={t.published}
            id="published"
            value={published}
            onChangeFunction={handlePublishedChange}
          />

          <TextInput
            type="text"
            labelName={t.publisher}
            id="publisher"
            value={publisher}
            onChangeFunction={handlePublisherChange}
          />

          <TextInput
            type="text"
            labelName={t.coverurl}
            id="coverurl"
            value={coverurl[0] !== false ? coverurl : ''}
            onChangeFunction={handleCoverurlChange}
            inputmode="url"
          />

          {book?.status === 'read' && (
            <StarRating rating={rating} setRating={setRating} />
          )}

          {book?.status === 'read' && (
            <TextArea
              type="textarea"
              labelName={t.reviewLabel}
              id="review"
              value={review}
              onChangeFunction={handleReviewChange}
            />
          )}

          {required && (
            <SelectInput
              labelName={t.savetolist}
              id="status"
              onChangeFunction={handleStatusChange}
              value={status}
              // icon
              options={[
                { value: 'reading', label: t.reading },
                { value: 'wantToRead', label: t.wanttoread },
                { value: 'read', label: t.haveread },
              ]}
            />
          )}
        </form>

        <div className="btn-group-wrapper">
          <div className="btn-group btn-group-two-col">
            <button
              className="btn btn-secondary w-100"
              onClick={() => toggleEdit()}
            >
              {t.cancel}
            </button>
            <button
              className="btn btn-primary w-100"
              type="button"
              onClick={handleSubmit}
              disabled={updatingBook}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBook;
